import { useTranslation } from 'react-i18next';

import GenericTabs from 'components/navigation/Tabs/Tabs';
import { NsKey } from 'enums/NsKey';

import { CollaboratorsManager } from 'back-office/shared/components/collaborators/CollaboratorsManager';

interface PersonneMoraleProfileTabsProps {
  personneMoraleId: string;
  /** Cards of the left column of the Identity tab (identity, bank accounts…). */
  leftColumn: React.ReactNode;
  /** Cards of the right column (contact, roles…). */
  rightColumn: React.ReactNode;
  membersCount?: number | null;
  /** Description under the Collaborators tab — the title stays on the tab
      itself (label + badge), no duplicate heading in the content. */
  membersDescription?: string;
  onMembersCountChange?: (count: number) => void;
  /** Content of the "Plateformes" tab (full details page) — absent = no tab. */
  plateformesTab?: React.ReactNode;
  plateformesCount?: number | null;
}

/**
 * Tabs of a personne morale profile — "Identité de la personne morale"
 * (two-column grid, slotted content), "Collaborateurs" (CollaboratorsManager) and an
 * optional "Plateformes" tab. Layout shared between the Gouvernance tab of a
 * plateforme, the full PM details page and the intermediary quick view.
 */
export const PersonneMoraleProfileTabs: React.FC<
  PersonneMoraleProfileTabsProps
> = ({
  personneMoraleId,
  leftColumn,
  rightColumn,
  membersCount,
  membersDescription,
  onMembersCountChange,
  plateformesTab,
  plateformesCount
}) => {
  const { t } = useTranslation(NsKey.PersonneMorale);

  return (
    <GenericTabs defaultTab="identite" fullWidth={false}>
      <GenericTabs.Item label={t('tabs.identity')} value="identite" />
      <GenericTabs.Item
        label={t('tabs.collaborators')}
        badge={membersCount ?? undefined}
        value="collaborateurs"
      />
      {plateformesTab != null && (
        <GenericTabs.Item
          label={t('tabs.plateformes')}
          badge={plateformesCount ?? undefined}
          value="plateformes"
        />
      )}

      <GenericTabs.Content value="identite">
        {/* Two balanced columns: no hole under a short card. */}
        <div className="grid grid-cols-1 items-start gap-6 pt-4 xl:grid-cols-2">
          <div className="flex flex-col gap-6">{leftColumn}</div>
          <div className="flex flex-col gap-6">{rightColumn}</div>
        </div>
      </GenericTabs.Content>

      <GenericTabs.Content value="collaborateurs">
        <div className="pt-4">
          <CollaboratorsManager
            personneMoraleId={personneMoraleId}
            titleDescription={
              membersDescription ?? t('tabs.membersDescription')
            }
            onMembersCountChange={onMembersCountChange}
          />
        </div>
      </GenericTabs.Content>

      {plateformesTab != null && (
        <GenericTabs.Content value="plateformes">
          <div className="pt-4">{plateformesTab}</div>
        </GenericTabs.Content>
      )}
    </GenericTabs>
  );
};
